import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Shield } from "lucide-react";
import { useAuth } from "@/AuthContext";

const links = [
  { to: "/coaches", label: "Find Coaches", id: "nav-coaches" },
  { to: "/camps", label: "Camps", id: "nav-camps" },
  { to: "/video-analysis", label: "Video Analysis", id: "nav-video" },
  { to: "/about", label: "About", id: "nav-about" },
];

export const Nav = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const authed = user && user !== true;

  return (
    <header className="sticky top-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <Shield className="w-6 h-6 text-[#00e5ff]" strokeWidth={2.5} />
          <span className="display text-2xl font-900 uppercase tracking-tighter font-bold">
            Goalie<span className="text-[#00e5ff]">Hub</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={`text-sm uppercase tracking-wide transition-colors duration-200 hover:text-[#00e5ff] ${
                loc.pathname === l.to ? "text-[#00e5ff]" : "text-zinc-300"
              }`}
            >
              {l.label}
            </Link>
          ))}
          {authed && user.role === "coach" && (
            <Link to="/coach/dashboard" data-testid="nav-coach-dashboard" className="text-sm uppercase tracking-wide text-zinc-300 hover:text-[#00e5ff] transition-colors duration-200">
              Dashboard
            </Link>
          )}
          {authed && user.role === "admin" && (
            <>
              <Link to="/admin/insights" data-testid="nav-admin-insights" className="text-sm uppercase tracking-wide text-zinc-300 hover:text-[#00e5ff] transition-colors duration-200">
                Insights
              </Link>
              <Link to="/admin/content" data-testid="nav-admin-content" className="text-sm uppercase tracking-wide text-zinc-300 hover:text-[#00e5ff] transition-colors duration-200">
                Content
              </Link>
              <Link to="/admin/coaches" data-testid="nav-admin-coaches" className="text-sm uppercase tracking-wide text-zinc-300 hover:text-[#00e5ff] transition-colors duration-200">
                Approvals
              </Link>
              <Link to="/admin/payouts" data-testid="nav-admin-payouts" className="text-sm uppercase tracking-wide text-zinc-300 hover:text-[#00e5ff] transition-colors duration-200">
                Payouts
              </Link>
            </>
          )}
          {authed && user.role === "goalie" && (
            <Link to="/progress" data-testid="nav-progress" className="text-sm uppercase tracking-wide text-zinc-300 hover:text-[#00e5ff] transition-colors duration-200">
              Progress
            </Link>
          )}
          {authed && user.role !== "coach" && (
            <Link to="/bookings" data-testid="nav-my-bookings" className="text-sm uppercase tracking-wide text-zinc-300 hover:text-[#00e5ff] transition-colors duration-200">
              My Bookings
            </Link>
          )}
          {authed ? (
            <button
              data-testid="nav-logout-btn"
              onClick={async () => { await logout(); nav("/"); }}
              className="text-sm uppercase tracking-wide text-zinc-500 hover:text-[#ff3b30] transition-colors duration-200"
            >
              Log out ({user.name.split(" ")[0]})
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <Link to="/login" data-testid="nav-login-btn" className="text-sm uppercase tracking-wide text-zinc-300 hover:text-[#00e5ff] transition-colors duration-200">Log in</Link>
              <Link to="/register" data-testid="nav-signup-btn" className="bg-[#00e5ff] text-black text-sm font-bold uppercase tracking-wide px-5 py-2 hover:bg-[#00b3cc] transition-colors duration-200">
                Sign up
              </Link>
            </div>
          )}
        </nav>

        <button className="md:hidden text-white" data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-black/95 px-6 py-4 flex flex-col gap-4">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-sm uppercase text-zinc-200">{l.label}</Link>
          ))}
          {authed && user.role === "coach" && <Link to="/coach/dashboard" onClick={() => setOpen(false)} className="text-sm uppercase text-zinc-200">Dashboard</Link>}
          {authed && user.role !== "coach" && <Link to="/bookings" onClick={() => setOpen(false)} className="text-sm uppercase text-zinc-200">My Bookings</Link>}
          {authed && user.role === "goalie" && <Link to="/progress" onClick={() => setOpen(false)} className="text-sm uppercase text-zinc-200">Progress</Link>}
          {authed && user.role === "admin" && (
            <>
              <Link to="/admin/insights" onClick={() => setOpen(false)} className="text-sm uppercase text-zinc-200">Insights</Link>
              <Link to="/admin/content" onClick={() => setOpen(false)} className="text-sm uppercase text-zinc-200">Content</Link>
              <Link to="/admin/coaches" onClick={() => setOpen(false)} className="text-sm uppercase text-zinc-200">Approvals</Link>
              <Link to="/admin/payouts" onClick={() => setOpen(false)} className="text-sm uppercase text-zinc-200">Payouts</Link>
            </>
          )}
          {authed ? (
            <button onClick={async () => { await logout(); setOpen(false); nav("/"); }} className="text-left text-sm uppercase text-[#ff3b30]">Log out</button>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="text-sm uppercase text-zinc-200">Log in</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="text-sm uppercase text-[#00e5ff]">Sign up</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
};
